import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';

import { ArrowDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMessages from '@/hooks/useMessages';
import MessageAreaPrologue from '@/modules/humanChat/components/MessageAreaPrologue/MessageAreaPrologue';
import { useComments } from '@/modules/humanChat/hooks/useComments';
import { useConversationEvents } from '@/modules/humanChat/hooks/useConversationEvents';
import { useConversation } from '@/modules/humanChat/hooks/useConversations';
import useRtmComments from '@/modules/humanChat/hooks/useRtmComments';
import { selectSessionId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { AuthorType, ConversationEvent, ConversationSource, Message } from '@/shared/types/chat';
import type { Comment } from '@/shared/types/comments';
import { DEFAULT_START_MESSAGE } from '@/util/constants';

import { BrainStream } from './BrainStream/BrainStream';
import { Events } from './ConversationActivity/Events';
import { MessageGroup } from './ConversationActivity/MessageGroup';
import { NoteEvent } from './ConversationActivity/NoteEvent';
import { Reminder } from './ConversationActivity/Reminder';
import { mergeAndSortData, shouldShowAvatar, shouldShowTime } from './helpers';
import { TimeElement } from './TimeElement/TimeElement';
import { TriggerLabel } from './TriggerLabel/TriggerLabel';

interface Props {
  isLoading: boolean;
  onEditNote: (comment: Comment) => void;
  onCancelNoteEdit: () => void;
}

export type TimeElementType = { time_element: boolean; created: string };

export type MessageOrEvent = Message | ConversationEvent | TimeElementType | Comment;

// Treat the view as "at the bottom" within this distance (px) so minor offsets
// from variable-height content still keep the thread pinned to the latest message.
const SCROLL_BOTTOM_THRESHOLD = 100;

const MessageArea = ({ isLoading, onEditNote, onCancelNoteEdit }: Props) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const userId = useSelector(selectUserId);
  const {
    conversation,
    reopenConversation,
    closeConversation,
    isExpired,
    isResolved,
    isSpam,
    isTrash,
    isExpiredAndResolved,
    isOpen,
  } = useConversation({ deskId, sessionId });

  const loadMoreRef = useRef(null);
  const oldScrollRef = useRef<number>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Whether the user is at (or near) the bottom of the thread. When true we keep
  // the view pinned to the latest message as content grows.
  const isAtBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const { messages, fetchNextPage, hasNextPage } = useMessages(deskId, sessionId);

  const { events } = useConversationEvents(deskId, sessionId);

  const { comments, deleteComment } = useComments(deskId, sessionId);
  useRtmComments(deskId, sessionId);

  const handleDeleteNote = useCallback(
    (commentId: string) => {
      onCancelNoteEdit();
      deleteComment(commentId);
    },
    [onCancelNoteEdit, deleteComment]
  );

  const messagesPages = useMemo(() => [...(messages?.pages ?? [])], [messages?.pages]);
  const flatMessages = useMemo(
    () => messagesPages.flatMap((page) => page.messages),
    [messagesPages]
  );

  const { hasAutostartTrigger, firstMessageTimestamp } = useMemo(() => {
    const firstMessage = flatMessages[flatMessages.length - 1];
    return {
      hasAutostartTrigger: firstMessage?.text === DEFAULT_START_MESSAGE,
      firstMessageTimestamp: firstMessage?.created,
    };
  }, [flatMessages]);

  // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
  const isMessage = (item: MessageOrEvent): item is Message => 'message_id' in item;

  const isTimeElement = (item: MessageOrEvent): item is TimeElementType => 'time_element' in item;

  const isComment = (item: MessageOrEvent): item is Comment => 'comment_id' in item;

  // for saving some iterations in flatMessages
  const messageIndexMap = useMemo(() => {
    const map = new Map();
    flatMessages.forEach((message, index) => {
      map.set(message.message_id, index);
    });
    return map;
  }, [flatMessages]);

  const newestTimestamp = useMemo(() => {
    const newestMessage = flatMessages[0]?.created;
    const newestEvent = events[0]?.created;
    const newestComment = comments[comments.length - 1]?.created;

    const timestamps = [newestMessage, newestEvent, newestComment].filter(Boolean);

    if (timestamps.length === 0) {
      return null;
    }

    return timestamps.reduce((newest, current) =>
      new Date(current).getTime() > new Date(newest).getTime() ? current : newest
    );
  }, [comments, events, flatMessages]);

  // Only visitor messages should trigger the "Unread messages" button.
  const newestVisitorTimestamp = useMemo(
    () => flatMessages.find((m) => m.author_type === AuthorType.VISITOR)?.created ?? null,
    [flatMessages]
  );

  // The operator's own sent message — used to snap the view to the bottom even
  // when they were scrolled up, so they always see what they just sent.
  const newestOwnTimestamp = useMemo(
    () => flatMessages.find((m) => m.author_id === userId)?.created ?? null,
    [flatMessages, userId]
  );

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
    isAtBottomRef.current = true;
    setShowScrollButton(false);
  }, [scrollToBottom]);

  // Only used as the onScroll handler, so it doesn't need to be memoized.
  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD;
    isAtBottomRef.current = atBottom;
    if (atBottom && showScrollButton) {
      setShowScrollButton(false);
    }
  };

  // Switching conversations reuses this component (the route only swaps the
  // sessionId), so reset the scroll state — otherwise a "scrolled up" flag from
  // the previous conversation leaks in and the new one opens mid-thread with a
  // stale unread button.
  useLayoutEffect(() => {
    isAtBottomRef.current = true;
    setShowScrollButton(false);
  }, [sessionId]);

  // Any new content arrived and the view is at the bottom — keep it pinned there.
  // useLayoutEffect runs before paint so there's no visible jump.
  useLayoutEffect(() => {
    if (!isLoading && newestTimestamp && isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [isLoading, newestTimestamp, scrollToBottom]);

  // A visitor message arrived while the operator is scrolled up — surface the
  // button so they can jump down without losing their reading position.
  useLayoutEffect(() => {
    if (!isLoading && newestVisitorTimestamp && !isAtBottomRef.current) {
      setShowScrollButton(true);
    }
  }, [isLoading, newestVisitorTimestamp]);

  // The operator sent a message — always jump to the bottom so they see it,
  // even if they had scrolled up (and clear any pending unread button).
  useLayoutEffect(() => {
    if (!isLoading && newestOwnTimestamp) {
      handleScrollToBottom();
    }
  }, [isLoading, newestOwnTimestamp, handleScrollToBottom]);

  // Catch async height growth that doesn't trigger a React render (attachment
  // images/media finishing layout after the initial paint).
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) {
        scrollToBottom();
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  // Handle scroll position after loading more messages (pre-paint to avoid a
  // flash). The bottom-pin effect above is a no-op here since loading older
  // messages sets isAtBottomRef to false.
  useLayoutEffect(() => {
    if (bodyRef.current && messages?.pages.length > 1) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight - oldScrollRef.current;
    }
  }, [messages?.pages.length]);

  const handleNextPageFetch = useCallback(() => {
    // The user is scrolling up to load older messages — stop pinning to bottom.
    isAtBottomRef.current = false;
    oldScrollRef.current = bodyRef.current.scrollHeight;
    return fetchNextPage();
  }, [fetchNextPage]);

  useIntersectionObserver({ target: loadMoreRef, onIntersect: handleNextPageFetch });

  const mergedData = useMemo(
    () => mergeAndSortData(flatMessages, [...events, ...comments]),
    [events, flatMessages, comments]
  );

  const firstTimeElementIndex = useMemo(() => mergedData.findIndex(isTimeElement), [mergedData]);

  type Segment =
    | { kind: 'time'; item: MessageOrEvent; index: number }
    | { kind: 'messages'; items: { item: MessageOrEvent; index: number }[] };

  const messageSegments = useMemo((): Segment[] => {
    const result: Segment[] = [];
    let batch: { item: MessageOrEvent; index: number }[] = [];

    mergedData.forEach((item, index) => {
      if (isTimeElement(item)) {
        if (batch.length > 0) {
          result.push({ kind: 'messages', items: batch });
          batch = [];
        }
        result.push({ kind: 'time', item, index });
      } else {
        batch.push({ item, index });
      }
    });
    if (batch.length > 0) {
      result.push({ kind: 'messages', items: batch });
    }
    return result;
  }, [mergedData]);

  const source: ConversationSource = useMemo(
    () => ({
      deskId: conversation?.desk_id,
      integrationId: conversation?.integration_id,
      channel: conversation?.channel,
      sessionId: conversation?.session_id,
    }),
    [
      conversation?.channel,
      conversation?.desk_id,
      conversation?.integration_id,
      conversation?.session_id,
    ]
  );

  const renderMessage = (message, index) => {
    const showTime = shouldShowTime({
      currentMessage: message,
      nextMessage: flatMessages[index - 1],
      index,
    });

    const showAvatar = shouldShowAvatar({
      currentMessage: message,
      nextMessage: flatMessages[index - 1],
      prevMessage: flatMessages[index + 1],
      index,
    });

    return (
      <MessageGroup
        message={message}
        key={message.message_id}
        showTime={showTime}
        showAvatar={showAvatar}
        source={source}
      />
    );
  };

  const renderMessageOrEvent = (item: MessageOrEvent, index: number) => {
    if (isMessage(item)) {
      if (item.text === DEFAULT_START_MESSAGE) {
        return null;
      }

      return item.body && 'reminder_id' in item.body ? (
        <Reminder key={item.message_id} minutes={item.body.minutes} />
      ) : (
        renderMessage(item, messageIndexMap.get(item.message_id))
      );
    }

    if (isTimeElement(item)) {
      return (
        <Fragment key={item.created}>
          <TimeElement created={item.created} />
          {hasAutostartTrigger && index === firstTimeElementIndex && (
            <TriggerLabel created={firstMessageTimestamp} />
          )}
        </Fragment>
      );
    }

    if (isComment(item)) {
      return (
        <NoteEvent
          key={item.comment_id}
          comment={item}
          onEdit={onEditNote}
          onDelete={handleDeleteNote}
        />
      );
    }

    return (
      <Events
        key={(item as ConversationEvent).event_id}
        event={item}
        visitorName={conversation?.context?.user?.display_name}
      />
    );
  };

  return (
    <>
      <MessageAreaPrologue
        conversation={conversation}
        reopenConversation={reopenConversation}
        closeConversation={closeConversation}
        isExpired={isExpired}
        isResolved={isResolved}
        isSpam={isSpam}
        isTrash={isTrash}
        isExpiredAndResolved={isExpiredAndResolved}
        isOpen={isOpen}
      />

      <div className="relative min-h-0 flex-1">
        <div
          className="h-full overflow-x-hidden overflow-y-auto px-4 pb-5"
          ref={bodyRef}
          onScroll={handleScroll}
        >
          <div className="wrap-anywhere [&>span+div]:mt-4" ref={contentRef}>
            {hasNextPage !== false && (
              <div className="mx-auto w-full max-w-3xl">
                <div ref={loadMoreRef}>
                  <Skeleton className="h-10 w-45" />
                </div>
              </div>
            )}
            {messageSegments.map((seg) =>
              seg.kind === 'time' ? (
                renderMessageOrEvent(seg.item, seg.index)
              ) : (
                <div
                  key={
                    (seg.items[0].item as { message_id?: string }).message_id ?? seg.items[0].index
                  }
                  className="mx-auto w-full max-w-3xl"
                >
                  {seg.items.map(({ item, index }) => renderMessageOrEvent(item, index))}
                </div>
              )
            )}
            <div className="mx-auto w-full max-w-3xl">
              <BrainStream sessionId={sessionId} />
            </div>
          </div>
        </div>
        {showScrollButton && (
          <div className="absolute right-0 bottom-0 left-0 px-4">
            <div className="mx-auto w-full max-w-3xl">
              <Button
                variant="outline-neutral"
                size="sm"
                className="gap-1.5 shadow-sm"
                onClick={handleScrollToBottom}
              >
                <ArrowDownIcon size={14} />
                {t('conversation.unread')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(MessageArea);
